/**
 * AppContext - shared UI state (modals, panels)
 *
 * Note: Logs are NOT stored here to avoid re-rendering the entire app on every log.
 * Components that display logs should subscribe to the logger directly.
 */

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { logger } from '@/logging';
import { type PerformanceStylesheet, createDefaultStylesheet } from '@/ui/performance/PerformanceStylesheet';

interface Inspecting {
  nodeId: string;
  instanceId: string;
  endpointId?: string;
}

interface AppContextValue {
  isDefInspectorOpen: boolean;
  isProjectBrowserOpen: boolean;
  openDefInspector: () => void;
  closeDefInspector: () => void;
  openProjectBrowser: () => void;
  closeProjectBrowser: () => void;
  // Node inspector
  inspecting: Inspecting | null;
  setInspecting: (inspecting: Inspecting | null) => void;
  // Modifier key state
  modifierKeyPressed: boolean;
  // Current project (name for display, path for file operations)
  projectName: string | null;
  projectPath: string | null;
  setProject: (name: string | null, path: string | null) => void;
  // Performance stylesheet
  stylesheet: PerformanceStylesheet;
  setStylesheet: (stylesheet: PerformanceStylesheet) => void;
  // Logging methods (don't trigger re-renders)
  logTrace: (source: string, message: string) => void;
  logInfo: (source: string, message: string) => void;
  logError: (source: string, message: string) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [isDefInspectorOpen, setIsDefInspectorOpen] = useState(false);
  const [isProjectBrowserOpen, setIsProjectBrowserOpen] = useState(false);
  const [inspecting, setInspecting] = useState<Inspecting>(null);
  const [modifierKeyPressed, setModifierKeyPressed] = useState(false);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [projectPath, setProjectPath] = useState<string | null>(null);
  const [stylesheet, setStylesheet] = useState<PerformanceStylesheet>(createDefaultStylesheet);

  const setProject = useCallback((name: string | null, path: string | null) => {
    setProjectName(name);
    setProjectPath(path);
  }, []);

  // Track modifier key state globally
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Meta' || e.key === 'Control' || e.key === 'Shift') {
        setModifierKeyPressed(true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Meta' || e.key === 'Control' || e.key === 'Shift') {
        setModifierKeyPressed(false);
      }
    };
    const handleBlur = () => setModifierKeyPressed(false);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const openDefInspector = useCallback(() => setIsDefInspectorOpen(true), []);
  const closeDefInspector = useCallback(() => setIsDefInspectorOpen(false), []);
  const openProjectBrowser = useCallback(() => setIsProjectBrowserOpen(true), []);
  const closeProjectBrowser = useCallback(() => setIsProjectBrowserOpen(false), []);


  const logTrace = useCallback((source: string, message: string) => {
    logger.trace(source, message);
  }, []);

  const logInfo = useCallback((source: string, message: string) => {
    logger.info(source, message);
  }, []);

  const logError = useCallback((source: string, message: string) => {
    logger.error(source, message);
  }, []);

  const value: AppContextValue = {
    isDefInspectorOpen,
    isProjectBrowserOpen,
    openDefInspector,
    closeDefInspector,
    openProjectBrowser,
    closeProjectBrowser,
    inspecting,
    setInspecting,
    modifierKeyPressed,
    projectName,
    projectPath,
    setProject,
    stylesheet,
    setStylesheet,
    logTrace,
    logInfo,
    logError
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppState(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppState must be used within AppStateProvider');
  }
  return context;
}
